import { courseService } from '@/services/courseService';
import { defineStore } from 'pinia';
import { CoursePlain, CourseSection, CourseItem, CompletedStep } from '@/entities/trainings';
import { isSection } from '@/ui/helpers/trainingsHelper';

interface CourseTakeState {
  course: CoursePlain | null;
  isLoading: boolean;
  activeItemIndex: number;
  sectionsCount: number;
  activeSection: CourseSection | null;
  activeSectionIndex: number;
  items: Array<CourseItem | CompletedStep>;
}

const defaultState = (): CourseTakeState => ({
  course: null,
  isLoading: false,
  activeItemIndex: 0,
  sectionsCount: 0,
  activeSection: null,
  activeSectionIndex: 0,
  items: [],
});

export const useCourseTakeStore = defineStore('courseTake', {
  state: defaultState,

  getters: {
    activeItem: state => state.items[state.activeItemIndex] || null,
    hasPrevious: state => state.activeItemIndex > 0,
    hasNext: state => state.activeItemIndex < state.items.length - 1,
    sections: state => state.items.filter(isSection),
    completedPercent: state => Math.round((state.activeItemIndex / (state.items.length - 1)) * 100),
  },

  actions: {
    async fetch(id: number) {
      this.isLoading = true;

      try {
        this.course = await courseService.loadForTake(id);
        this.initAfterLoad();
      } finally {
        this.isLoading = false;
      }
    },

    async submit() {
      this.isLoading = true;

      try {
        await courseService.submitCourse(this.course!.id);
      } finally {
        this.isLoading = false;
      }
    },

    initAfterLoad() {
      this.items = [
        ...this.course!.items,
        { type: 'completed' } as CompletedStep,
      ];

      this.sectionsCount = this.sections.length;
      this.setActiveItemIndex(0);
    },

    setActiveItemIndex(index: number) {
      this.activeItemIndex = index;

      if (this.activeItem && isSection(this.activeItem)) {
        this.activeSection = this.activeItem;
        this.activeSectionIndex = this.sections.indexOf(this.activeSection) + 1;
      }
    },

    previous() {
      if (this.hasPrevious) {
        this.setActiveItemIndex(this.activeItemIndex - 1);
      }
    },

    next() {
      if (this.hasNext) {
        this.setActiveItemIndex(this.activeItemIndex + 1);
      }
    },
  },
});
